import type {EagColumnState} from '../types/EagColumnState';
import type {EagGridTableProps} from '../types/EagGridTableProps';
import {columnToInitialState} from './columnToInitialState';
import {flattenColumnConfig} from './flattenColumnConfig';

/**
 * Computes the column states to seed into the store for a given column config.
 *
 * Pass `liveColumnStates` to preserve the user's active sort across an **in-place
 * activePreset update** (e.g. a preset Save re-applying the saved view, where the
 * dataQuery is unchanged). Sort lives ONLY in columnStates — it is intentionally not
 * mirrored into dataQuery.order (that would trigger a column-reset cascade) — so
 * re-seeding sort from {@link columnToInitialState} (which reads it from dataQuery.order)
 * would wipe the live sort and spuriously re-dirty the just-saved preset. Carrying the
 * live `sort`/`sortIndex` forward avoids that.
 *
 * Pass `null` on a real preset switch/load: the new preset's sort (carried in its
 * dataQuery.order, hence in `columnToInitialState`) is applied as-is.
 *
 * @example
 * // preset switch — take everything from the config
 * computeSeededColumnStates(columnConfig, null);
 *
 * // in-place Save — keep the user's live sort, reset the rest
 * computeSeededColumnStates(columnConfig, store.getState().columnStates);
 */
export function computeSeededColumnStates(
  columnConfig: EagGridTableProps['columnConfig'],
  liveColumnStates: EagColumnState[] | null
): EagColumnState[] {
  const liveByKey = liveColumnStates ? new Map(liveColumnStates.map((cs) => [cs.key, cs])) : null;

  return flattenColumnConfig(columnConfig).map((col, index) => {
    const initial = columnToInitialState(col, index);
    const live = liveByKey?.get(initial.key);
    return live ? {...initial, sort: live.sort, sortIndex: live.sortIndex} : initial;
  });
}
